import * as React from 'react';
import { AdminGroup, AdminGroupDraft, FormList } from 'Interfaces/Admin';
import { HtmlZipType, removeHtmlZip } from 'api/adminApi';
import { useAppDispatch } from 'index';
import { setFormListHasZip } from 'reducers/admin/meta';
import { upsertAdminGroup } from 'reducers/admin/groups';

/**
 *  The optional HTML bundle a group can carry. It may belong either to the
 *  group itself or to the form list it uses, which decides where a view or a
 *  removal has to be aimed. Ported from GroupView's attach/view/remove handlers.
 */
export const useHtmlZip = (
  existing: AdminGroup | undefined,
  draft: AdminGroupDraft,
  formList: FormList | undefined,
  onDraftChange: (patch: Partial<AdminGroupDraft>) => void,
) => {
  const dispatch = useAppDispatch();
  const [pendingZip, setPendingZip] = React.useState<string | null>(null);

  React.useEffect(() => setPendingZip(null), [existing?.id]);

  const hasZip = !!draft.hasZip || formList?.hasZip === true || !!pendingZip;

  const target = (): { type: HtmlZipType; id: number } | null => {
    if (existing && (existing.hasZip || (pendingZip && !draft.surveyId))) {
      return { type: 'group', id: existing.id };
    }
    if (draft.surveyId) {
      return { type: 'survey', id: draft.surveyId };
    }
    return null;
  };

  const select = (file: File | undefined) => {
    if (!file || !file.type.match('application/zip')) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      setPendingZip(typeof result === 'string' ? result : null);
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    // A zip that was only picked, never saved, just needs forgetting.
    if (pendingZip) {
      setPendingZip(null);
      return;
    }
    const t = target();
    if (!t) {
      return;
    }
    const response = await removeHtmlZip(t.type, t.id);
    if (response.status !== 'OK') {
      return;
    }
    if (t.type === 'survey') {
      dispatch(setFormListHasZip({ id: t.id, hasZip: false }));
    } else if (existing) {
      dispatch(upsertAdminGroup({ ...existing, hasZip: false, htmlZip: null }));
    }
    onDraftChange({ hasZip: false, htmlZip: null });
  };

  return { pendingZip, hasZip, target, select, remove };
};
